import { useMerchantSlug } from "@/utils/hooks/useMerchantSlug";
import { useNavigate } from "react-router-dom";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Customer } from "@/types/user";

interface CustomerContextType {
  customer: Customer | null;
  login: (customerData: Customer) => void;
  logout: () => void;
}

const CustomerContext = createContext<CustomerContextType | undefined>(
  undefined,
);

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
};

interface CustomerProviderProps {
  children: ReactNode;
}

export const CustomerProvider = ({ children }: CustomerProviderProps) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const merchantSlug = useMerchantSlug();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("CustomerProvider mounted");
    const storedCustomerData = localStorage.getItem("customer_data");
    if (storedCustomerData) {
      console.log("Found stored customer data");
      const customerData: Customer = JSON.parse(storedCustomerData);
      setCustomer(customerData);
    } else {
      console.log("No customer data found in localStorage");
    }
  }, []);

  const login = (customerData: Customer) => {
    console.log("Logging in customer:", customerData);
    setCustomer(customerData);
    localStorage.setItem("customer_data", JSON.stringify(customerData));
  };

  const logout = () => {
    console.log("Logging out customer");
    setCustomer(null);
    localStorage.removeItem("customer_data");

    navigate(`${merchantSlug}`);
  };

  return (
    <CustomerContext.Provider value={{ customer, login, logout }}>
      {children}
    </CustomerContext.Provider>
  );
};

export { CustomerContext };
